/**
 * Steerco snapshot vocabulary + helpers.
 *
 * The exec-facing Steerco tab renders a per-function status matrix. Each track
 * carries an optional `steerco` object (see the CONTRACT below). The backend
 * `steerco` field is being added in parallel, so the generated `DashboardTrack`
 * type may not expose it yet — `getSteerco` reads it defensively via a cast.
 *
 * TODO: once the backend ships `steerco` in the generated schema, drop the
 * local `Steerco`/`SteercoChip` types here and the cast in `getSteerco`, and
 * import the generated type from `@/lib/api` instead.
 *
 * Colours reuse the RAG palette (the brand's data/status exception): done =
 * green, in progress = amber, at risk = red, not started = muted grey. Every
 * chip also carries its text label, so colour is never the only signal.
 */

import { type DashboardTrack } from "@/lib/api";

export type SteercoState = "done" | "in_progress" | "at_risk" | "not_started";

/** A single status chip: a short label plus a state that drives its colour. */
export interface SteercoChip {
  label: string;
  state: SteercoState;
}

/** The full per-track Steerco snapshot (all fields optional on the wire). */
export interface Steerco {
  champion: SteercoChip;
  ideation: SteercoChip;
  use_case_def: SteercoChip;
  /** Per-track strategic choice — see PATH_TO_VALUE_OPTIONS ("" = unset). */
  path_to_value: string;
  comment: string;
  /** Program track only — the snapshot's action-title. */
  headline: string;
}

/** A track with the (possibly not-yet-generated) `steerco` field attached. */
type TrackWithSteerco = DashboardTrack & { steerco?: Steerco | null };

const EMPTY_CHIP: SteercoChip = { label: "", state: "not_started" };

/** A safe, fully-populated default so the UI never touches undefined fields. */
export const EMPTY_STEERCO: Steerco = {
  champion: { ...EMPTY_CHIP },
  ideation: { ...EMPTY_CHIP },
  use_case_def: { ...EMPTY_CHIP },
  path_to_value: "",
  comment: "",
  headline: "",
};

/**
 * Read a track's `steerco` object defensively, filling any missing field with
 * its default. Returns a complete `Steerco` so callers can render/edit without
 * null-checks.
 */
export function getSteerco(track: DashboardTrack): Steerco {
  const raw = (track as TrackWithSteerco).steerco ?? undefined;
  if (!raw) {
    return { ...EMPTY_STEERCO };
  }
  return {
    champion: { ...EMPTY_CHIP, ...raw.champion },
    ideation: { ...EMPTY_CHIP, ...raw.ideation },
    use_case_def: { ...EMPTY_CHIP, ...raw.use_case_def },
    path_to_value: raw.path_to_value ?? "",
    comment: raw.comment ?? "",
    headline: raw.headline ?? "",
  };
}

/** Human-readable label for a Steerco state. */
export function steercoStateLabel(state: SteercoState): string {
  switch (state) {
    case "done":
      return "Done";
    case "in_progress":
      return "In progress";
    case "at_risk":
      return "At risk";
    default:
      return "Not started";
  }
}

/**
 * Canonical per-track "path to value" choices (value → display label). Empty
 * string means unset. Order is canonical and shared with the backend contract.
 */
export const PATH_TO_VALUE_OPTIONS: { value: string; label: string }[] = [
  { value: "buy", label: "Buy a point solution" },
  { value: "vibe_code", label: "Vibe-code to validate" },
  { value: "build", label: "Build internally" },
  { value: "enterprise_ai", label: "Enterprise AI" },
];

/** Display label for a path-to-value option, or "—" for empty/unknown. */
export function pathToValueLabel(value: string): string {
  return PATH_TO_VALUE_OPTIONS.find((o) => o.value === value)?.label ?? "—";
}

/** Options for the state `InlineSelect` (structurally an `Option`). */
export const STEERCO_STATE_OPTIONS: { value: SteercoState; label: string }[] = [
  { value: "done", label: "Done" },
  { value: "in_progress", label: "In progress" },
  { value: "at_risk", label: "At risk" },
  { value: "not_started", label: "Not started" },
];

/** Dot-colour class for a Steerco state (used by the legend). */
export function steercoDotClass(state: SteercoState): string {
  switch (state) {
    case "done":
      return "bg-rag-green";
    case "in_progress":
      return "bg-rag-amber";
    case "at_risk":
      return "bg-rag-red";
    default:
      return "bg-muted-foreground/40";
  }
}

/**
 * Tailwind classes for a solid status chip. Amber is light, so it takes dark
 * text for contrast; green/red take white; not-started is a muted grey.
 */
export function steercoChipClass(state: SteercoState): string {
  const base = "inline-block rounded-sm px-2 py-0.5 text-xs uppercase";
  switch (state) {
    case "done":
      return `${base} bg-rag-green text-[#0D1314]`;
    case "in_progress":
      return `${base} bg-rag-amber text-[#0D1314]`;
    case "at_risk":
      return `${base} bg-rag-red text-[#0D1314]`;
    default:
      return `${base} bg-muted text-foreground`;
  }
}
